import { useEffect, useMemo, useState } from "react";
import {
  equalTo,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import { auth, db } from "../firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore";
import { useLanguageStore } from "../stores/useLanguageStore";
import { usePanelsStore } from "../stores/usePanelsStore";
import CloseButton from "./CloseButton.jsx";
import ImageUploader from "./ImageUploader.jsx";
import { showToast } from "./NotifyToast.jsx";
import TrekkinConfirm from "./TrekkinConfirm.jsx";
import { sendCustomBroadcastPush } from "../utils/sendCustomBroadcastPush.js";
import { partnerPaymentMethodLabel } from "../utils/partnerPayments.js";

const cash = (v) =>
  `${Number(v || 0)
    .toFixed(2)
    .replace(".", ",")} €`;
const daysBetween = (from, to) => {
  const a = new Date(`${from}T12:00:00`),
    b = new Date(`${to}T12:00:00`);
  return Number.isFinite(a.getTime()) && Number.isFinite(b.getTime())
    ? Math.max(1, Math.round((b - a) / 86400000) + 1)
    : 0;
};
const normalizeSizes = (item) => {
  if (Array.isArray(item?.sizeInventory)) {
    return item.sizeInventory
      .filter((entry) => String(entry?.size || "").trim() && Number(entry?.quantity || 0) > 0)
      .map((entry) => String(entry.size).trim());
  }
  const source = Array.isArray(item?.sizes) ? item.sizes : [item?.size];
  return [
    ...new Set(
      source
        .flatMap((value) => String(value || "").split(","))
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
};
const emptySize = () => ({ size: "", quantity: 1 });
const BIKE_CATEGORIES = ["mountain", "road", "gravel", "electric", "city", "kids", "tandem", "other"];
const categoryLabel = (category, tr) => ({
  mountain: tr("BTT / Montanha", "MTB / Mountain"),
  road: tr("Estrada", "Road"),
  gravel: "Gravel",
  electric: tr("Elétrica", "Electric"),
  city: tr("Cidade / Urbana", "City / Urban"),
  kids: tr("Criança", "Kids"),
  tandem: "Tandem",
  other: tr("Outra", "Other"),
}[category] || tr("Sem categoria", "Uncategorised"));
const statusLabel = (status, tr) => ({
  requested: tr("Pedido enviado", "Request sent"),
  proposal_sent: tr("Proposta recebida", "Proposal received"),
  accepted: tr("Pedido aceite — aguarda pagamento", "Request accepted — awaiting payment"),
  customer_accepted: tr("Proposta aceite — aguarda pagamento", "Proposal accepted — awaiting payment"),
  changes_requested: tr("Alterações pedidas", "Changes requested"),
  paid: tr("Pago", "Paid"),
  completed: tr("Concluído", "Completed"),
  rejected: tr("Recusado", "Rejected"),
  cancelled: tr("Cancelado", "Cancelled"),
  payment_failed: tr("Pagamento falhou", "Payment failed"),
  payment_pending: tr("Email de pagamento enviado", "Payment email sent"),
  availability_failed: tr("Indisponível para estas datas", "Unavailable for these dates"),
}[status] || status || "—");

const locationText = (request, kind, tr) => {
  const mode = request?.[`${kind}Mode`];
  if (mode !== "address") {
    return kind === "pickup"
      ? tr("Levantamento no parceiro", "Pick-up at partner")
      : tr("Devolução no parceiro", "Return to partner");
  }
  const location = request?.[`${kind}Location`];
  return request?.[`${kind}Address`] || location?.placeName ||
    (Number.isFinite(Number(location?.lat)) && Number.isFinite(Number(location?.lon))
      ? `${Number(location.lat).toFixed(5)}, ${Number(location.lon).toFixed(5)}`
      : tr("Morada selecionada no mapa", "Address selected on map"));
};

export default function PanelBikeRental(props = {}) {
  const user = useAuthStore((s) => s.user);
  const language = useLanguageStore((s) => s.language);
  const { resetToMap, setActivePanel } = usePanelsStore();
  const partnerMode = props.mode === "partner";
  const tr = (pt, en) => String(language || "").toLowerCase().startsWith("en") ? en : pt;
  const waypoint = props.waypoint || null;
  const partnerUid = String(
    props.partnerUid || waypoint?.partnerUserUid || user?.uid || "",
  );
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [catalog, setCatalog] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [allocationDraft, setAllocationDraft] = useState({ accommodationId: "", bikeId: "", quantities: {} });
  const [requests, setRequests] = useState([]);
  const [remoteAvailability, setRemoteAvailability] = useState({});
  const [selected, setSelected] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingBikeId, setEditingBikeId] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [draft, setDraft] = useState({
    brand: "",
    model: "",
    category: "mountain",
    description: "",
    sizeInventory: [emptySize()],
    pricePerDay: "",
    imageUrl: "",
    active: true,
  });
  const [form, setForm] = useState(() => ({
    category: "",
    bikeId: "",
    selectedSize: "",
    startDate: "",
    endDate: "",
    pickupMode: "partner",
    pickupLocation: null,
    returnMode: "partner",
    returnLocation: null,
    notes: "",
    ...(props.bikeRentalDraft || {}),
  }));
  const [proposal, setProposal] = useState({
    startDate: "",
    endDate: "",
    pricePerDay: "",
    deliveryFee: "",
    collectionFee: "",
    message: "",
  });

  useEffect(() => {
    const result = props?.callbackResult;
    if (
      result?.mode !== "bike_rental_pickup" &&
      result?.mode !== "bike_rental_return"
    )
      return;
    const location = {
      lat: result.lat,
      lon: result.lon,
      placeName: result.placeName || "",
    };
    setForm((current) => ({
      ...current,
      [result.mode === "bike_rental_pickup"
        ? "pickupLocation"
        : "returnLocation"]: location,
    }));
  }, [props?.callbackResult]);

  const chooseLocation = (kind) =>
    setActivePanel("mapSelectPoint", {
      returnPanel: "bikeRental",
      mode: kind === "pickup" ? "bike_rental_pickup" : "bike_rental_return",
      returnProps: { mode: "new", bikeRentalDraft: form, waypoint, partnerUid },
    });

  useEffect(() => {
    if (!partnerUid) return;
    return onValue(ref(db, `bikeRentalCatalog/${partnerUid}`), (snap) =>
      setCatalog(
        Object.entries(snap.val() || {})
          .map(([id, v]) => ({ id, ...v }))
          .sort((a, b) => Number(a.order ?? 999999) - Number(b.order ?? 999999) || Number(a.createdAt || 0) - Number(b.createdAt || 0)),
      ),
    );
  }, [partnerUid]);
  useEffect(() => {
    if (!partnerUid) return undefined;
    const allocationPath = partnerMode
      ? `bikeRentalAllocations/${partnerUid}`
      : waypoint?.accommodationRentalEnabled && waypoint?.id
        ? `bikeRentalAllocations/${partnerUid}/${waypoint.id}`
        : "";
    const stopAllocations = allocationPath
      ? onValue(ref(db, allocationPath), (snap) => setAllocations(partnerMode ? (snap.val() || {}) : { [waypoint.id]: snap.val() || {} }))
      : () => {};
    if (!partnerMode) return stopAllocations;
    const stopWaypoints = onValue(ref(db, "waypoints"), (snap) => {
      setAccommodations(Object.entries(snap.val() || {}).map(([id, value]) => ({ id, ...(value || {}) })).filter((item) => item.accommodationRentalEnabled === true && item.rentalPartnerUid === partnerUid));
    });
    return () => { stopAllocations(); stopWaypoints(); };
  }, [partnerMode, partnerUid, waypoint?.accommodationRentalEnabled, waypoint?.id]);
  useEffect(() => {
    if (!user?.uid || props.mode === "new") return;
    const root = ref(db, "bikeRentalRequests");
    const source = isAdmin
      ? root
      : query(
          root,
          orderByChild(partnerMode ? "partnerUid" : "ownerUid"),
          equalTo(user.uid),
        );
    return onValue(source, (snap) =>
      setRequests(
        Object.entries(snap.val() || {})
          .map(([id, v]) => ({ id, ...v }))
          .filter((request) => isAdmin || (partnerMode ? request.hiddenForPartner !== true : request.hiddenForOwner !== true))
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
      ),
    );
  }, [user?.uid, partnerMode, isAdmin, props.mode]);
  const activeCatalog = useMemo(() => catalog.filter((x) => x.active !== false).map((item) => {
    if (!waypoint?.accommodationRentalEnabled || !waypoint?.id) return item;
    const allocated = allocations?.[waypoint.id]?.[item.id]?.sizeInventory;
    return { ...item, sizeInventory: Array.isArray(allocated) ? allocated : [] };
  }).filter((item) => normalizeSizes(item).length > 0), [allocations, catalog, waypoint?.accommodationRentalEnabled, waypoint?.id]);
  const filteredCatalog = useMemo(() => categoryFilter
    ? activeCatalog.filter((item) => String(item.category || "other") === categoryFilter)
    : activeCatalog, [activeCatalog, categoryFilter]);
  const bike = activeCatalog.find((x) => x.id === form.bikeId);
  const availableSizes = normalizeSizes(bike);
  const sizeAvailability = useMemo(() => {
    if (!bike) return {};
    const inventory = Array.isArray(bike.sizeInventory) && bike.sizeInventory.length
      ? bike.sizeInventory
      : normalizeSizes(bike).map((size) => ({ size, quantity: bike.quantity || 1 }));
    return Object.fromEntries(inventory.map((entry) => {
      const size = String(entry.size || "").trim();
      const capacity = Math.max(0, Number(entry.quantity || 0));
      if (!form.startDate || !form.endDate) return [size, { capacity, reserved: 0, remaining: capacity }];
      return [size, remoteAvailability[size] || { capacity, reserved: 0, remaining: capacity }];
    }));
  }, [bike, form.startDate, form.endDate, remoteAvailability]);
  useEffect(() => {
    if (props.mode !== "new" || !bike?.id || !form.startDate || !form.endDate) {
      setRemoteAvailability({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const token = await auth.currentUser?.getIdToken?.();
        const base = String(import.meta.env.VITE_API_BASE || "https://trekkin-api-snowy-sunset-6669.fly.dev").replace(/\/$/, "");
        const entries = await Promise.all(availableSizes.map(async (size) => {
          const response = await fetch(`${base}/bike-rental/availability`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ partnerUid, bikeId: bike.id, bike: { size }, waypointId: waypoint?.id || "", startDate: form.startDate, endDate: form.endDate }) });
          if (!response.ok) throw new Error("availability_failed");
          return [size, await response.json()];
        }));
        if (!cancelled) setRemoteAvailability(Object.fromEntries(entries));
      } catch { if (!cancelled) setRemoteAvailability({}); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [props.mode, partnerUid, bike?.id, form.startDate, form.endDate, waypoint?.id, availableSizes.join("|")]);
  useEffect(() => {
    if (form.selectedSize && sizeAvailability[form.selectedSize]?.remaining <= 0) {
      setForm((current) => ({ ...current, selectedSize: "" }));
    }
  }, [form.selectedSize, sizeAvailability]);
  const numberOfDays = daysBetween(form.startDate, form.endDate);
  const estimated = numberOfDays * Number(bike?.pricePerDay || 0);
  const allocationBike = catalog.find((item) => item.id === allocationDraft.bikeId);
  const allocationInventory = Array.isArray(allocationBike?.sizeInventory)
    ? allocationBike.sizeInventory
    : normalizeSizes(allocationBike).map((size) => ({ size, quantity: allocationBike?.quantity || 1 }));
  const availableForAccommodation = (entry) => {
    const allocatedElsewhere = Object.entries(allocations || {}).reduce((sum, [accommodationId, rows]) => {
      if (accommodationId === allocationDraft.accommodationId) return sum;
      const row = rows?.[allocationDraft.bikeId];
      const sizeRow = Array.isArray(row?.sizeInventory) ? row.sizeInventory.find((item) => item.size === entry.size) : null;
      return sum + Number(sizeRow?.quantity || 0);
    }, 0);
    return Math.max(0, Number(entry.quantity || 0) - allocatedElsewhere);
  };
  const saveAllocation = async () => {
    const accommodation = accommodations.find((item) => item.id === allocationDraft.accommodationId);
    if (!user?.uid || !accommodation || !allocationBike) return showToast(tr("Seleciona o alojamento e a bicicleta.", "Select the accommodation and bicycle."), "error");
    const sizeInventory = allocationInventory.map((entry) => ({ size: entry.size, quantity: Math.min(Math.max(0, Number(allocationDraft.quantities[entry.size]) || 0), availableForAccommodation(entry)) })).filter((entry) => entry.quantity > 0);
    const path = `bikeRentalAllocations/${user.uid}/${accommodation.id}/${allocationBike.id}`;
    if (!sizeInventory.length) await remove(ref(db, path));
    else await set(ref(db, path), { accommodationId: accommodation.id, accommodationName: accommodation.name || "", bikeId: allocationBike.id, sizeInventory, updatedAt: Date.now() });
    showToast(tr("Stock do alojamento atualizado.", "Accommodation stock updated."), "success");
  };
  const saveBike = async (e) => {
    e.preventDefault();
    const sizeInventory = draft.sizeInventory
      .map((entry) => ({ size: String(entry.size || "").trim(), quantity: Math.max(0, Number(entry.quantity) || 0) }))
      .filter((entry) => entry.size && entry.quantity > 0);
    const sizes = [...new Set(sizeInventory.map((entry) => entry.size))];
    if (
      !user?.uid ||
      !draft.brand.trim() ||
      !draft.model.trim() ||
      !sizes.length ||
      Number(draft.pricePerDay) <= 0
    )
      return showToast(
        tr(
          "Preenche marca, modelo, tamanhos disponíveis e preço.",
          "Enter brand, model, available sizes and price.",
        ),
        "error",
      );
    setBusy(true);
    try {
      const itemRef = editingBikeId
        ? ref(db, `bikeRentalCatalog/${user.uid}/${editingBikeId}`)
        : push(ref(db, `bikeRentalCatalog/${user.uid}`));
      const previous = editingBikeId ? catalog.find((item) => item.id === editingBikeId) : null;
      await update(itemRef, {
        brand: draft.brand.trim(),
        model: draft.model.trim(),
        category: draft.category || "other",
        description: draft.description.trim(),
        size: sizes.join(", "),
        sizes,
        sizeInventory,
        pricePerDay: Number(draft.pricePerDay),
        quantity: sizeInventory.reduce((sum, entry) => sum + entry.quantity, 0),
        imageUrl: draft.imageUrl || "",
        active: true,
        createdAt: previous?.createdAt || Date.now(),
        order: previous?.order ?? catalog.length,
        updatedAt: Date.now(),
      });
      setDraft({
        brand: "",
        model: "",
        category: "mountain",
        description: "",
        sizeInventory: [emptySize()],
        pricePerDay: "",
        imageUrl: "",
        active: true,
      });
      setEditingBikeId("");
      showToast(tr(editingBikeId ? "Bicicleta atualizada." : "Bicicleta adicionada.", editingBikeId ? "Bicycle updated." : "Bicycle added."), "success");
    } catch (error) {
      console.error("[bike-rental] save catalogue item failed", error);
      showToast(tr("Não foi possível guardar a bicicleta.", "Could not save the bicycle."), "error");
    } finally {
      setBusy(false);
    }
  };
  const editBike = (item) => {
    setEditingBikeId(item.id);
    setDraft({
      brand: item.brand || "",
      model: item.model || "",
      category: item.category || "other",
      description: item.description || "",
      sizeInventory: Array.isArray(item.sizeInventory) && item.sizeInventory.length
        ? item.sizeInventory.map((entry) => ({ size: entry.size || "", quantity: entry.quantity || 1 }))
        : [{ size: item.size || "", quantity: item.quantity || 1 }],
      pricePerDay: String(item.pricePerDay ?? ""),
      imageUrl: item.imageUrl || "",
      active: item.active !== false,
    });
  };
  const cancelBikeEdit = () => {
    setEditingBikeId("");
    setDraft({ brand: "", model: "", category: "mountain", description: "", sizeInventory: [emptySize()], pricePerDay: "", imageUrl: "", active: true });
  };
  const moveBike = async (item, direction) => {
    const index = catalog.findIndex((entry) => entry.id === item.id);
    const other = catalog[index + direction];
    if (!other || !user?.uid) return;
    try {
      await Promise.all([
        update(ref(db, `bikeRentalCatalog/${user.uid}/${item.id}`), { order: index + direction, updatedAt: Date.now() }),
        update(ref(db, `bikeRentalCatalog/${user.uid}/${other.id}`), { order: index, updatedAt: Date.now() }),
      ]);
    } catch (error) {
      console.error("[bike-rental] reorder failed", error);
      showToast(tr("Não foi possível alterar a ordem.", "Could not change the order."), "error");
    }
  };
  const submitRequest = async (e) => {
    e.preventDefault();
    if (
      !user?.uid ||
      !partnerUid ||
      !bike ||
      !numberOfDays ||
      !form.selectedSize
    )
      return showToast(
        tr(
          "Seleciona a bicicleta, o tamanho e as datas.",
          "Select a bicycle, size and dates.",
        ),
        "error",
      );
    if (sizeAvailability[form.selectedSize]?.remaining <= 0)
      return showToast(
        tr("Esta bicicleta já está reservada para as datas selecionadas.", "This bicycle is already booked for the selected dates."),
        "error",
      );
    const pickupMode = waypoint?.accommodationRentalEnabled ? "accommodation" : form.pickupMode;
    const returnMode = waypoint?.accommodationRentalEnabled ? "accommodation" : form.returnMode;
    if (pickupMode === "address" && !form.pickupLocation)
      return showToast(
        tr(
          "Seleciona no mapa a morada de entrega.",
          "Select the delivery address on the map.",
        ),
        "error",
      );
    if (returnMode === "address" && !form.returnLocation)
      return showToast(
        tr(
          "Seleciona no mapa a morada de recolha.",
          "Select the collection address on the map.",
        ),
        "error",
      );
    setBusy(true);
    try {
      const now = Date.now(),
        requestRef = push(ref(db, "bikeRentalRequests"));
      await set(requestRef, {
        ownerUid: user.uid,
        ownerEmail: user.email || "",
        ownerName: user.name || user.displayName || "",
        partnerUid,
        partnerName: waypoint?.name || "",
        waypointId: waypoint?.id || null,
        bikeId: bike.id,
        bike: { brand: bike.brand, model: bike.model, size: form.selectedSize },
        bikeCategory: bike.category || "other",
        bikeImageUrl: bike.imageUrl || "",
        bikeDescription: bike.description || "",
        startDate: form.startDate,
        endDate: form.endDate,
        days: numberOfDays,
        pricePerDay: Number(bike.pricePerDay),
        total: estimated,
        pickupMode,
        accommodationName: waypoint?.accommodationRentalEnabled ? (waypoint?.name || "") : "",
        pickupLocation:
          pickupMode === "address" ? form.pickupLocation : null,
        pickupAddress:
          pickupMode === "address"
            ? form.pickupLocation?.placeName ||
              `${form.pickupLocation?.lat}, ${form.pickupLocation?.lon}`
            : "",
        returnMode,
        returnLocation:
          returnMode === "address" ? form.returnLocation : null,
        returnAddress:
          returnMode === "address"
            ? form.returnLocation?.placeName ||
              `${form.returnLocation?.lat}, ${form.returnLocation?.lon}`
            : "",
        notes: form.notes.trim(),
        status: "requested",
        createdAt: now,
        updatedAt: now,
        history: { [now]: { status: "requested", at: now, byUid: user.uid } },
      });
      sendCustomBroadcastPush({
        notificationType: "bike_rental_requested",
        notificationTitle: tr("Novo pedido de aluguer", "New rental request"),
        notificationBody: `${bike.brand || ""} ${bike.model || ""} · ${form.startDate} → ${form.endDate}`.trim(),
        userIds: [partnerUid],
        extraData: { requestId: requestRef.key, type: "bike_rental_requested" },
      }).catch(() => {});
      showToast(
        tr("Pedido de aluguer enviado.", "Rental request sent."),
        "success",
      );
      setActivePanel("bikeRental", { mode: "customer" });
    } finally {
      setBusy(false);
    }
  };
  const openRequest = (row) => {
    setSelected(row);
    setProposal({
      startDate: row.proposedStartDate || row.startDate || "",
      endDate: row.proposedEndDate || row.endDate || "",
      pricePerDay: String(row.proposedPricePerDay ?? row.pricePerDay ?? ""),
      deliveryFee: String(row.proposedDeliveryFee ?? row.deliveryFee ?? ""),
      collectionFee: String(row.proposedCollectionFee ?? row.collectionFee ?? ""),
      message: row.proposalMessage || "",
    });
  };
  const sendPaymentEmail = async (requestId) => {
    const token = await auth.currentUser?.getIdToken?.();
    const base = String(import.meta.env.VITE_API_BASE || "https://trekkin-api-snowy-sunset-6669.fly.dev").replace(/\/$/, "");
    const response = await fetch(`${base}/bike-rental/create-payment-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || ""}` },
      body: JSON.stringify({ requestId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.paymentEmailSent) throw new Error(data.error || tr("Não foi possível enviar o email de pagamento.", "Could not send the payment email."));
    showToast(tr("Link de pagamento enviado por email.", "Payment link sent by email."), "success");
  };
  const partnerAction = async (status) => {
    if (!selected?.id || !user?.uid || busy) return;
    const now = Date.now();
    let values = {
      status,
      updatedAt: now,
      [`history/${now}`]: { status, at: now, byUid: user.uid },
    };
    if (status === "proposal_sent" || status === "accepted") {
      const days = daysBetween(proposal.startDate, proposal.endDate),
        ppd = Number(proposal.pricePerDay),
        deliveryFee = selected.pickupMode === "address" ? Number(proposal.deliveryFee || 0) : 0,
        collectionFee = selected.returnMode === "address" ? Number(proposal.collectionFee || 0) : 0;
      if (!days || ppd <= 0)
        return showToast(
          tr("Indica datas e preço válidos.", "Enter valid dates and price."),
          "error",
        );
      if (deliveryFee < 0 || collectionFee < 0)
        return showToast(
          tr("Os custos de entrega e recolha não podem ser negativos.", "Delivery and collection fees cannot be negative."),
          "error",
        );
      const rentalSubtotal = days * ppd;
      const calculatedTotal = rentalSubtotal + deliveryFee + collectionFee;
      if (status === "accepted") {
        values = {
          ...values,
          startDate: proposal.startDate,
          endDate: proposal.endDate,
          days,
          pricePerDay: ppd,
          rentalSubtotal,
          deliveryFee,
          collectionFee,
          total: calculatedTotal,
        };
      } else {
      values = {
        ...values,
        proposedStartDate: proposal.startDate,
        proposedEndDate: proposal.endDate,
        proposedDays: days,
        proposedPricePerDay: ppd,
        proposedRentalSubtotal: rentalSubtotal,
        proposedDeliveryFee: deliveryFee,
        proposedCollectionFee: collectionFee,
        proposedTotal: calculatedTotal,
        proposalMessage: proposal.message.trim(),
      };
      }
    }
    setBusy(true);
    try {
      await update(ref(db, `bikeRentalRequests/${selected.id}`), values);
      if (selected.ownerUid) {
        const message = status === "accepted"
          ? tr("O teu aluguer foi aceite. Enviámos o pagamento para o teu email.", "Your rental was accepted. We sent the payment link to your email.")
          : status === "proposal_sent"
            ? tr("Recebeste uma nova proposta para o aluguer.", "You received a new rental proposal.")
            : status === "completed"
              ? tr("O teu aluguer foi marcado como concluído.", "Your rental was marked as completed.")
              : tr("O pedido de aluguer foi recusado.", "The rental request was declined.");
        sendCustomBroadcastPush({
          notificationType: "bike_rental_status",
          notificationTitle: tr("Atualização do aluguer", "Rental update"),
          notificationBody: message,
          userIds: [selected.ownerUid],
          extraData: { requestId: selected.id, status, type: "bike_rental_status" },
        }).catch(() => {});
      }
      if (status === "accepted") {
        try {
          await sendPaymentEmail(selected.id);
        } catch (emailError) {
          showToast(emailError?.message || tr("Pedido aceite, mas não foi possível enviar o email. Podes reenviá-lo na ficha.", "Request accepted, but the email could not be sent. You can resend it from the request."), "error");
          setSelected((current) => ({ ...current, ...values }));
          return;
        }
      }
      showToast(tr("Pedido atualizado.", "Request updated."), "success");
      setSelected(null);
    } catch (error) {
      console.error("[bike-rental] partner action failed", error);
      showToast(tr("Não foi possível atualizar o pedido.", "Could not update the request."), "error");
    } finally {
      setBusy(false);
    }
  };
  const customerAction = async (status) => {
    if (!selected?.id || busy) return;
    const now = Date.now();
    const values = {
      status,
      updatedAt: now,
      [`history/${now}`]: { status, at: now, byUid: user.uid },
    };
    if (status === "customer_accepted")
      Object.assign(values, {
        startDate: selected.proposedStartDate || selected.startDate,
        endDate: selected.proposedEndDate || selected.endDate,
        days: selected.proposedDays || selected.days,
        pricePerDay: selected.proposedPricePerDay || selected.pricePerDay,
        rentalSubtotal: selected.proposedRentalSubtotal ?? selected.rentalSubtotal ?? (Number(selected.proposedDays || selected.days || 0) * Number(selected.proposedPricePerDay || selected.pricePerDay || 0)),
        deliveryFee: selected.proposedDeliveryFee ?? selected.deliveryFee ?? 0,
        collectionFee: selected.proposedCollectionFee ?? selected.collectionFee ?? 0,
        total: selected.proposedTotal ?? selected.total,
      });
    setBusy(true);
    try {
      await update(ref(db, `bikeRentalRequests/${selected.id}`), values);
      if (selected.partnerUid) {
        const message = status === "customer_accepted"
          ? tr("O cliente aceitou a proposta de aluguer.", "The customer accepted the rental proposal.")
          : status === "changes_requested"
            ? tr("O cliente pediu alterações à proposta.", "The customer requested changes to the proposal.")
            : tr("O cliente recusou a proposta.", "The customer declined the proposal.");
        sendCustomBroadcastPush({
          notificationType: "bike_rental_status",
          notificationTitle: tr("Atualização do aluguer", "Rental update"),
          notificationBody: message,
          userIds: [selected.partnerUid],
          extraData: { requestId: selected.id, status, type: "bike_rental_status" },
        }).catch(() => {});
      }
      setSelected((s) => ({ ...s, ...values }));
      if (status === "customer_accepted") await sendPaymentEmail(selected.id);
      else showToast(tr("Resposta enviada.", "Response sent."), "success");
    } catch (error) {
      console.error("[bike-rental] customer action failed", error);
      showToast(error?.message || tr("Não foi possível atualizar o pedido.", "Could not update the request."), "error");
    } finally {
      setBusy(false);
    }
  };

  const selectedCatalogBike = selected
    ? catalog.find((item) => item.id === selected.bikeId) || null
    : null;
  const selectedImage = selected?.bikeImageUrl || selectedCatalogBike?.imageUrl || "";
  const selectedDescription = selected?.bikeDescription || selectedCatalogBike?.description || "";
  const selectedCategory = selected?.bikeCategory || selectedCatalogBike?.category || "";
  const selectedDays = selected
    ? Number(selected.proposedDays ?? selected.days ?? daysBetween(selected.startDate, selected.endDate))
    : 0;
  const selectedPricePerDay = Number(selected?.proposedPricePerDay ?? selected?.pricePerDay ?? 0);
  const selectedRentalSubtotal = Number(
    selected?.proposedRentalSubtotal ?? selected?.rentalSubtotal ?? selectedDays * selectedPricePerDay,
  );
  const selectedDeliveryFee = selected?.proposedDeliveryFee ?? selected?.deliveryFee;
  const selectedCollectionFee = selected?.proposedCollectionFee ?? selected?.collectionFee;
  const selectedTotal = Number(
    selected?.proposedTotal ?? selected?.total ??
      (selectedRentalSubtotal + Number(selectedDeliveryFee || 0) + Number(selectedCollectionFee || 0)),
  );
  const resendPaymentEmail = async () => {
    setBusy(true);
    try {
      await sendPaymentEmail(selected.id);
    } catch (error) {
      console.error("[bike-rental] payment failed", error);
      showToast(
        error?.message || tr("Não foi possível iniciar o pagamento.", "Could not start payment."),
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteRentalRequest = async () => {
    if (!selected?.id || !user?.uid || busy) return;
    setBusy(true);
    try {
      if (isAdmin) {
        await remove(ref(db, `bikeRentalRequests/${selected.id}`));
      } else {
        const now = Date.now();
        const hiddenField = partnerMode ? "hiddenForPartner" : "hiddenForOwner";
        await update(ref(db, `bikeRentalRequests/${selected.id}`), {
          [hiddenField]: true,
          [`${hiddenField}At`]: now,
          updatedAt: now,
        });
      }
      setRequests((current) => current.filter((request) => request.id !== selected.id));
      setDeleteConfirmOpen(false);
      setSelected(null);
      showToast(
        isAdmin
          ? tr("Pedido eliminado definitivamente.", "Request permanently deleted.")
          : tr("Pedido eliminado da tua lista.", "Request removed from your list."),
        "success",
      );
    } catch (error) {
      console.error("[bike-rental] delete request failed", error);
      showToast(tr("Não foi possível eliminar o pedido.", "Could not delete the request."), "error");
    } finally {
      setBusy(false);
    }
  };

  if (props.mode === "new")
    return (
      <div className="guide-request-modal tk-bike-service-request" style={requestPanel}>
        <header className="tk-standard-panel-hero tk-bike-request-hero">
          <div className="tk-standard-panel-close"><CloseButton onClick={resetToMap} /></div>
          <div className="tk-standard-panel-brand"><img src={`${import.meta.env.BASE_URL || "/"}icons/logo.png`} alt=""/><strong>Trekkin</strong></div>
          <h2>{tr("Alugar bicicleta", "Rent a bicycle")}</h2>
          <p>{tr("Escolhe a bicicleta, as datas e os locais de entrega e devolução.", "Choose the bicycle, dates, and pick-up and return locations.")}</p>
        </header>
        <form className="tk-bike-service-request-form" onSubmit={submitRequest}>
          <section className="tk-bike-service-form-section">
            <h3>{tr("Escolha da bicicleta", "Choose a bicycle")}</h3>
            <label>
              {tr("Categoria", "Category")}
              <select value={categoryFilter} onChange={(e) => {
                setCategoryFilter(e.target.value);
                setForm((current) => ({ ...current, bikeId: "", selectedSize: "" }));
              }}>
                <option value="">{tr("Todas as categorias", "All categories")}</option>
                {BIKE_CATEGORIES.filter((category) => activeCatalog.some((item) => String(item.category || "other") === category)).map((category) => (
                  <option key={category} value={category}>{categoryLabel(category, tr)}</option>
                ))}
              </select>
            </label>
            <label>
              {tr("Bicicleta", "Bicycle")}
              <select
                value={form.bikeId}
                onChange={(e) =>
                  setForm({ ...form, bikeId: e.target.value, selectedSize: "" })
                }
              >
                <option value="">{tr("Selecionar", "Select")}</option>
                {filteredCatalog.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.brand} {x.model} · {cash(x.pricePerDay)}/
                    {tr("dia", "day")}
                  </option>
                ))}
              </select>
            </label>
            {bike && (
              <article className="tk-bike-rental-choice-preview">
                {bike.imageUrl && <img src={bike.imageUrl} alt={`${bike.brand} ${bike.model}`} />}
                <div>
                  <small>{categoryLabel(bike.category, tr)}</small>
                  <strong>{bike.brand} {bike.model}</strong>
                  {bike.description && <p>{bike.description}</p>}
                </div>
              </article>
            )}
            {bike && (
              <label>
                {tr("Tamanho", "Size")}
                <select
                  value={form.selectedSize}
                  onChange={(e) =>
                    setForm({ ...form, selectedSize: e.target.value })
                  }
                >
                  <option value="">
                    {tr("Selecionar tamanho", "Select size")}
                  </option>
                  {availableSizes.map((size) => (
                    <option key={size} value={size} disabled={sizeAvailability[size]?.remaining <= 0}>
                      {size}{sizeAvailability[size]?.remaining <= 0
                        ? tr(" — indisponível nestas datas", " — unavailable for these dates")
                        : form.startDate && form.endDate
                          ? ` — ${sizeAvailability[size]?.remaining || 0} ${tr("disponível", "available")}`
                          : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="tk-bike-service-field-row">
              <label>
                {tr("Data de entrega/levantamento", "Delivery/pick-up date")}
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
              </label>
              <label>
                {tr("Data de recolha/devolução", "Collection/return date")}
                <input
                  type="date"
                  min={form.startDate || undefined}
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="tk-bike-service-field-row">
              <div className="tk-bike-rental-location-group">
                <label>
                  {tr("Entrega ou levantamento", "Delivery or pick-up")}
                  <select
                    value={form.pickupMode}
                    onChange={(e) => setForm({ ...form, pickupMode: e.target.value, pickupLocation: e.target.value === "partner" ? null : form.pickupLocation })}
                  >
                    <option value="partner">{waypoint?.accommodationRentalEnabled ? tr("Levantamento no alojamento", "Pick up at accommodation") : tr("Levantamento no parceiro", "Pick up at partner")}</option>
                    <option value="address">{tr("Entrega numa morada específica", "Delivery to a specific address")}</option>
                  </select>
                </label>
                {form.pickupMode === "address" && (
                  <button className="tk-bike-service-location-button" type="button" onClick={() => chooseLocation("pickup")}>
                    {form.pickupLocation ? `📍 ${form.pickupLocation.placeName || `${form.pickupLocation.lat}, ${form.pickupLocation.lon}`}` : `📍 ${tr("Selecionar morada de entrega no mapa", "Select delivery address on map")}`}
                  </button>
                )}
                {form.pickupMode === "partner" && (
                  <div className="tk-bike-rental-partner-address">
                    <span aria-hidden="true">📍</span>
                    <span>{waypoint?.address || waypoint?.morada || waypoint?.placeName || waypoint?.name || tr("Morada do parceiro", "Partner address")}</span>
                  </div>
                )}
              </div>
              <div className="tk-bike-rental-location-group">
                <label>
                  {tr("Recolha ou devolução", "Collection or return")}
                  <select
                    value={form.returnMode}
                    onChange={(e) => setForm({ ...form, returnMode: e.target.value, returnLocation: e.target.value === "partner" ? null : form.returnLocation })}
                  >
                    <option value="partner">{waypoint?.accommodationRentalEnabled ? tr("Devolução no alojamento", "Return at accommodation") : tr("Devolução no parceiro", "Return to partner")}</option>
                    <option value="address">{tr("Recolha numa morada específica", "Collection from a specific address")}</option>
                  </select>
                </label>
                {form.returnMode === "address" && (
                  <button className="tk-bike-service-location-button" type="button" onClick={() => chooseLocation("return")}>
                    {form.returnLocation ? `📍 ${form.returnLocation.placeName || `${form.returnLocation.lat}, ${form.returnLocation.lon}`}` : `📍 ${tr("Selecionar morada de recolha no mapa", "Select collection address on map")}`}
                  </button>
                )}
                {form.returnMode === "partner" && (
                  <div className="tk-bike-rental-partner-address">
                    <span aria-hidden="true">📍</span>
                    <span>{waypoint?.address || waypoint?.morada || waypoint?.placeName || waypoint?.name || tr("Morada do parceiro", "Partner address")}</span>
                  </div>
                )}
              </div>
            </div>
            {(form.pickupMode === "address" || form.returnMode === "address") && (
              <div className="tk-bike-rental-address-cost-notice" role="note">
                <span aria-hidden="true">ℹ️</span>
                <span>
                  {tr(
                    "O custo da entrega e/ou recolha na morada selecionada será calculado pelo parceiro e apresentado na proposta antes do pagamento.",
                    "The delivery and/or collection cost for the selected address will be calculated by the partner and shown in the proposal before payment.",
                  )}
                </span>
              </div>
            )}
            <label>
              {tr("Observações", "Notes")}
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
            {bike && numberOfDays > 0 ? (
              <b>
                {numberOfDays} {tr("dias", "days")} · {cash(estimated)}
              </b>
            ) : null}
          </section>
          <button
            className="tk-bike-service-submit"
            disabled={busy || !activeCatalog.length}
          >
            {tr("Enviar pedido de aluguer", "Send rental request")}
          </button>
        </form>
      </div>
    );

  return (
    <div className="tk-theme-card tk-bike-management-panel" style={panel}>
      <header className="tk-standard-panel-hero">
        <div className="tk-standard-panel-close"><CloseButton onClick={resetToMap} /></div>
        <div className="tk-standard-panel-brand"><img src={`${import.meta.env.BASE_URL || "/"}icons/logo.png`} alt=""/><strong>Trekkin</strong></div>
        <h2>{partnerMode
            ? tr("Catálogo e alugueres", "Catalogue and rentals")
            : tr("Os meus alugueres", "My rentals")}</h2>
        <p>{partnerMode
            ? tr("Gere as bicicletas e os pedidos de aluguer.", "Manage bicycles and rental requests.")
            : tr("Consulta e acompanha os teus alugueres de bicicletas.", "Review and track your bicycle rentals.")}</p>
      </header>
      {partnerMode && (
        <form onSubmit={saveBike} className="tk-bike-service-form-section">
          <h3>{editingBikeId
            ? tr("Editar bicicleta do catálogo", "Edit catalogue bicycle")
            : tr("Adicionar bicicleta ao catálogo", "Add bicycle to catalogue")}</h3>
          <div className="tk-bike-service-field-row">
            <label>
              {tr("Marca", "Brand")}
              <input
                value={draft.brand}
                onChange={(e) => setDraft({ ...draft, brand: e.target.value })}
              />
            </label>
            <label>
              {tr("Modelo", "Model")}
              <input
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
              />
            </label>
          </div>
          <label>
            {tr("Categoria", "Category")}
            <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
              {BIKE_CATEGORIES.map((category) => <option key={category} value={category}>{categoryLabel(category, tr)}</option>)}
            </select>
          </label>
          <label>
            {tr("Descrição", "Description")}
            <textarea rows="4" value={draft.description} placeholder={tr("Características, utilização recomendada e equipamento incluído…", "Features, recommended use and included equipment…")} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </label>
          <label>
            {tr("Preço por dia (€)", "Price per day (€)")}
            <input
              type="number"
              min="0"
              step="0.01"
              value={draft.pricePerDay}
              onChange={(e) => setDraft({ ...draft, pricePerDay: e.target.value })}
            />
          </label>
          <div className="tk-bike-rental-sizes">
            <strong>{tr("Tamanhos e quantidades disponíveis", "Available sizes and quantities")}</strong>
            {draft.sizeInventory.map((entry, index) => (
              <div className="tk-bike-rental-size-row" key={index}>
                <label>
                  {tr("Tamanho", "Size")}
                  <input
                    value={entry.size}
                    placeholder={tr("Ex.: S, M, L ou 54", "E.g. S, M, L or 54")}
                    onChange={(e) => setDraft((current) => ({ ...current, sizeInventory: current.sizeInventory.map((item, i) => i === index ? { ...item, size: e.target.value } : item) }))}
                  />
                </label>
                <label>
                  {tr("Quantidade", "Quantity")}
                  <input
                    type="number"
                    min="1"
                    value={entry.quantity}
                    onChange={(e) => setDraft((current) => ({ ...current, sizeInventory: current.sizeInventory.map((item, i) => i === index ? { ...item, quantity: e.target.value } : item) }))}
                  />
                </label>
                {draft.sizeInventory.length > 1 && <button type="button" className="tk-bike-rental-remove-size" onClick={() => setDraft((current) => ({ ...current, sizeInventory: current.sizeInventory.filter((_, i) => i !== index) }))}>×</button>}
              </div>
            ))}
            <button type="button" className="tk-bike-service-location-button" onClick={() => setDraft((current) => ({ ...current, sizeInventory: [...current.sizeInventory, emptySize()] }))}>＋ {tr("Adicionar tamanho", "Add size")}</button>
          </div>
          <div className="tk-bike-service-photo-field">
            <span>{tr("Fotografia da bicicleta", "Bicycle photo")}</span>
            {!draft.imageUrl && <ImageUploader eventId={`bike-rental-${user?.uid || "new"}`} label={tr("Adicionar fotografia", "Add photo")} hidePreview onUploaded={(url) => setDraft((current) => ({ ...current, imageUrl: url }))}/>} 
            {draft.imageUrl && <div className="tk-bike-service-photo-grid"><div className="tk-bike-service-photo"><img src={draft.imageUrl} alt={tr("Fotografia da bicicleta", "Bicycle photo")}/><button type="button" onClick={() => setDraft((current) => ({ ...current, imageUrl: "" }))}>×</button></div></div>}
          </div>
          <button className="tk-bike-service-submit" disabled={busy}>
            {editingBikeId ? tr("Guardar alterações", "Save changes") : tr("Adicionar ao catálogo", "Add to catalogue")}
          </button>
          {editingBikeId && <button type="button" className="tk-bike-rental-secondary" onClick={cancelBikeEdit}>{tr("Cancelar edição", "Cancel editing")}</button>}
          <div style={grid}>
            {catalog.map((x) => (
              <div key={x.id} style={card}>
                {x.imageUrl && <img src={x.imageUrl} alt={`${x.brand} ${x.model}`} style={catalogImage}/>} 
                <b>
                  {x.brand} {x.model}
                </b>
                <small>{categoryLabel(x.category, tr)}</small>
                {x.description && <p className="tk-bike-rental-catalog-description">{x.description}</p>}
                <div className="tk-bike-rental-inventory-summary">
                  {(Array.isArray(x.sizeInventory) && x.sizeInventory.length
                    ? x.sizeInventory
                    : [{ size: x.size, quantity: x.quantity || 1 }]
                  ).map((entry, index) => (
                    <span key={`${entry.size}-${index}`}>
                      {tr("Tamanho", "Size")}: <b>{entry.size}</b> · {tr("Quantidade", "Quantity")}: <b>{entry.quantity}</b>
                    </span>
                  ))}
                </div>
                <span>{cash(x.pricePerDay)}/{tr("dia", "day")}</span>
                <div className="tk-bike-rental-catalog-actions">
                  <button type="button" disabled={catalog[0]?.id === x.id} onClick={() => moveBike(x, -1)} aria-label={tr("Mover para cima", "Move up")}>↑</button>
                  <button type="button" disabled={catalog[catalog.length - 1]?.id === x.id} onClick={() => moveBike(x, 1)} aria-label={tr("Mover para baixo", "Move down")}>↓</button>
                  <button type="button" onClick={() => editBike(x)}>{tr("Editar", "Edit")}</button>
                  <button
                    type="button"
                    onClick={() =>
                      update(ref(db, `bikeRentalCatalog/${user.uid}/${x.id}`), {
                        active: x.active === false,
                        updatedAt: Date.now(),
                      })
                    }
                  >
                    {x.active === false
                      ? tr("Ativar", "Activate")
                      : tr("Pausar", "Pause")}
                  </button>{" "}
                  <button
                    type="button"
                    onClick={() =>
                      remove(ref(db, `bikeRentalCatalog/${user.uid}/${x.id}`))
                    }
                  >
                    {tr("Eliminar", "Delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </form>
      )}
      {partnerMode && (
        <section className="tk-bike-service-form-section tk-bike-rental-allocation">
          <h3>{tr("Bicicletas permanentes nos alojamentos", "Bicycles permanently at accommodation")}</h3>
          <p>{tr("Atribui parte do stock do catálogo a um alojamento. Essas bicicletas ficam disponíveis para levantamento nesse local.", "Assign catalogue stock to an accommodation. Those bicycles become available for pick-up at that location.")}</p>
          {!accommodations.length ? <small>{tr("Ainda não tens alojamentos associados pelo administrador.", "You do not yet have accommodation assigned by the administrator.")}</small> : <>
            <label>{tr("Alojamento", "Accommodation")}
              <select value={allocationDraft.accommodationId} onChange={(e) => setAllocationDraft({ accommodationId: e.target.value, bikeId: "", quantities: {} })}>
                <option value="">{tr("Selecionar alojamento", "Select accommodation")}</option>
                {accommodations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>{tr("Bicicleta do catálogo", "Catalogue bicycle")}
              <select value={allocationDraft.bikeId} disabled={!allocationDraft.accommodationId} onChange={(e) => {
                const bikeId = e.target.value;
                const current = allocations?.[allocationDraft.accommodationId]?.[bikeId]?.sizeInventory || [];
                setAllocationDraft((draftValue) => ({ ...draftValue, bikeId, quantities: Object.fromEntries(current.map((entry) => [entry.size, entry.quantity])) }));
              }}>
                <option value="">{tr("Selecionar bicicleta", "Select bicycle")}</option>
                {catalog.filter((item) => item.active !== false).map((item) => <option key={item.id} value={item.id}>{item.brand} {item.model}</option>)}
              </select>
            </label>
            {allocationBike && <div className="tk-bike-rental-sizes">{allocationInventory.map((entry) => { const maximum = availableForAccommodation(entry); return <label key={entry.size}>{tr("Tamanho", "Size")}: {entry.size} <small>({tr("disponível para este alojamento", "available for this accommodation")}: {maximum})</small><input type="number" min="0" max={maximum} value={allocationDraft.quantities[entry.size] ?? 0} onChange={(e) => setAllocationDraft((current) => ({ ...current, quantities: { ...current.quantities, [entry.size]: e.target.value } }))}/></label>; })}</div>}
            <button type="button" className="tk-bike-service-submit" disabled={!allocationBike} onClick={saveAllocation}>{tr("Guardar stock neste alojamento", "Save stock at this accommodation")}</button>
          </>}
        </section>
      )}
      <div style={grid}>
        {requests.map((r) => (
          <button
            key={r.id}
            type="button"
            className="tk-bike-rental-request-card"
            style={{ ...card, textAlign: "left" }}
            onClick={() => openRequest(r)}
          >
            {(r.bikeImageUrl || catalog.find((item) => item.id === r.bikeId)?.imageUrl) && (
              <img className="tk-bike-rental-request-thumb" src={r.bikeImageUrl || catalog.find((item) => item.id === r.bikeId)?.imageUrl} alt="" />
            )}
            <span className="tk-bike-rental-request-info">
              <b>{r.bike?.brand} {r.bike?.model} · {r.bike?.size}</b>
              <span>{r.startDate} → {r.endDate} · {r.proposedDays || r.days || daysBetween(r.startDate, r.endDate)} {tr("dias", "days")}</span>
              <span>{cash(r.proposedTotal ?? r.total)}</span>
              <small>{statusLabel(r.status, tr)}</small>
            </span>
          </button>
        ))}
      </div>
      {!requests.length && (
        <div className="tk-bike-rental-empty">
          <span aria-hidden="true">✓</span>
          <strong>{tr("Ainda não existem pedidos", "There are no requests yet")}</strong>
          <small>{partnerMode
            ? tr("Os pedidos de aluguer enviados pelos clientes aparecerão aqui.", "Rental requests submitted by customers will appear here.")
            : tr("Quando fizeres um pedido de aluguer, poderás acompanhá-lo aqui.", "When you submit a rental request, you can follow it here.")}
          </small>
        </div>
      )}
      {selected && (
        <div className="tk-bike-rental-detail-backdrop" style={backdrop}>
          <div className="tk-theme-card tk-bike-rental-detail-modal" style={modal}>
            <header className="tk-bike-rental-detail-header">
              <h3>{selected.bike?.brand} {selected.bike?.model} · {selected.bike?.size}</h3>
              <CloseButton onClick={() => setSelected(null)} />
            </header>
            <div className="tk-bike-rental-detail-summary">
              {selectedImage && <img src={selectedImage} alt={`${selected.bike?.brand || ""} ${selected.bike?.model || ""}`} />}
              <div>
                <span className="tk-bike-rental-status">{statusLabel(selected.status, tr)}</span>
                {selectedCategory && <b>{categoryLabel(selectedCategory, tr)}</b>}
                <span>{tr("Tamanho", "Size")}: <b>{selected.bike?.size || "—"}</b></span>
                <span>{tr("Preço por dia", "Price per day")}: <b>{cash(selected.proposedPricePerDay ?? selected.pricePerDay)}</b></span>
              </div>
            </div>
            {selectedDescription && <p className="tk-bike-rental-detail-description">{selectedDescription}</p>}
            <div className="tk-bike-rental-detail-facts">
              <div><small>{tr("Período", "Period")}</small><b>{selected.proposedStartDate || selected.startDate} → {selected.proposedEndDate || selected.endDate}</b></div>
              <div><small>{tr("Duração", "Duration")}</small><b>{selected.proposedDays || selected.days || daysBetween(selected.startDate, selected.endDate)} {tr("dias", "days")}</b></div>
              <div><small>{tr("Aluguer", "Rental")}</small><b>{cash(selectedRentalSubtotal)}</b></div>
              {selected.pickupMode === "address" && <div><small>{tr("Entrega", "Delivery")}</small><b>{selectedDeliveryFee == null ? tr("A calcular pelo parceiro", "To be calculated by partner") : cash(selectedDeliveryFee)}</b></div>}
              {selected.returnMode === "address" && <div><small>{tr("Recolha", "Collection")}</small><b>{selectedCollectionFee == null ? tr("A calcular pelo parceiro", "To be calculated by partner") : cash(selectedCollectionFee)}</b></div>}
              <div><small>{tr("Total", "Total")}</small><b>{cash(selectedTotal)}</b></div>
            </div>
            {partnerMode && ["paid", "completed"].includes(selected.status) && (
              <section className="tk-request-partner-payout">
                <div><span>{tr("Valor a receber da Trekkin", "Amount receivable from Trekkin")}</span><b>{cash(selected?.payment?.partnerAmount ?? selectedTotal)}</b></div>
                <small className={selected?.payment?.partnerPayout?.paid ? "is-paid" : "is-pending"}>
                  {selected?.payment?.partnerPayout?.paid
                    ? `${tr("Pago", "Paid")} · ${partnerPaymentMethodLabel(selected.payment.partnerPayout.method, language)} · ${new Date(selected.payment.partnerPayout.paidAt).toLocaleDateString(language === "en" ? "en-GB" : "pt-PT")}`
                    : tr("Por pagar pela Trekkin", "Payment pending from Trekkin")}
                </small>
              </section>
            )}
            <div className="tk-bike-rental-locations" style={card}>
              <div><small>{tr("Entrega / levantamento", "Delivery / pick-up")}</small><b>{locationText(selected, "pickup", tr)}</b></div>
              <div><small>{tr("Recolha / devolução", "Collection / return")}</small><b>{locationText(selected, "return", tr)}</b></div>
            </div>
            {selected.notes && <div className="tk-bike-rental-notes"><small>{tr("Observações", "Notes")}</small><p>{selected.notes}</p></div>}
            {partnerMode &&
              !["accepted", "customer_accepted", "payment_pending", "paid", "completed", "cancelled", "rejected"].includes(
                selected.status,
              ) && (
                <div className="tk-bike-rental-proposal-form">
                  <div className="tk-bike-service-field-row">
                    <label>
                      {tr("Levantamento proposto", "Proposed pick-up")}
                      <input
                        type="date"
                        value={proposal.startDate}
                        onChange={(e) =>
                          setProposal({
                            ...proposal,
                            startDate: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {tr("Devolução proposta", "Proposed return")}
                      <input
                        type="date"
                        value={proposal.endDate}
                        onChange={(e) =>
                          setProposal({ ...proposal, endDate: e.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label>
                    {tr("Preço por dia (€)", "Price per day (€)")}
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={proposal.pricePerDay}
                      onChange={(e) =>
                        setProposal({
                          ...proposal,
                          pricePerDay: e.target.value,
                        })
                      }
                    />
                  </label>
                  {selected.pickupMode === "address" && (
                    <label>
                      {tr("Custo de entrega (€)", "Delivery fee (€)")}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={proposal.deliveryFee}
                        onChange={(e) => setProposal({ ...proposal, deliveryFee: e.target.value })}
                      />
                    </label>
                  )}
                  {selected.returnMode === "address" && (
                    <label>
                      {tr("Custo de recolha (€)", "Collection fee (€)")}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={proposal.collectionFee}
                        onChange={(e) => setProposal({ ...proposal, collectionFee: e.target.value })}
                      />
                    </label>
                  )}
                  <div className="tk-bike-rental-proposal-total">
                    <span>{tr("Aluguer", "Rental")}: <b>{cash(daysBetween(proposal.startDate, proposal.endDate) * Number(proposal.pricePerDay || 0))}</b></span>
                    <span>{tr("Total proposto", "Proposed total")}: <b>{cash(daysBetween(proposal.startDate, proposal.endDate) * Number(proposal.pricePerDay || 0) + Number(proposal.deliveryFee || 0) + Number(proposal.collectionFee || 0))}</b></span>
                  </div>
                  <label>
                    {tr("Mensagem", "Message")}
                    <textarea
                      value={proposal.message}
                      onChange={(e) =>
                        setProposal({ ...proposal, message: e.target.value })
                      }
                    />
                  </label>
                  <div style={actions}>
                    <button type="button" disabled={busy} onClick={() => partnerAction("accepted")}>
                      {tr("Aceitar pedido", "Accept request")}
                    </button>
                    <button type="button" disabled={busy} onClick={() => partnerAction("proposal_sent")}>
                      {tr("Enviar proposta", "Send proposal")}
                    </button>
                    <button type="button" disabled={busy} onClick={() => partnerAction("rejected")}>
                      {tr("Recusar", "Reject")}
                    </button>
                  </div>
                </div>
              )}
            {!partnerMode && selected.status === "proposal_sent" && (
              <div style={actions}>
                <button type="button" disabled={busy} onClick={() => customerAction("customer_accepted")}>
                  {tr("Aceitar proposta", "Accept proposal")}
                </button>
                <button type="button" disabled={busy} onClick={() => customerAction("changes_requested")}>
                  {tr("Pedir alteração", "Request changes")}
                </button>
                <button type="button" disabled={busy} onClick={() => customerAction("rejected")}>
                  {tr("Recusar", "Reject")}
                </button>
              </div>
            )}
            {!partnerMode &&
              ["accepted", "customer_accepted", "payment_pending", "payment_failed"].includes(
                selected.status,
              ) && (
                <button
                  className="tk-bike-service-submit"
                  onClick={resendPaymentEmail}
                  disabled={busy}
                >
                  {tr("Reenviar email de pagamento", "Resend payment email")}
                </button>
              )}
            {partnerMode && selected.status === "paid" && (
              <button type="button" onClick={() => partnerAction("completed")}>
                {tr("Marcar como concluído", "Mark completed")}
              </button>
            )}
            <button
              type="button"
              className="tk-bike-rental-delete-request"
              disabled={busy}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              {tr("Eliminar pedido", "Delete request")}
            </button>
          </div>
        </div>
      )}
      <TrekkinConfirm
        visible={deleteConfirmOpen}
        title={tr("Eliminar pedido de aluguer?", "Delete rental request?")}
        text={tr(
          isAdmin
            ? "O pedido será eliminado definitivamente da base de dados."
            : "O pedido deixa de aparecer na tua lista. O registo é mantido para o outro interveniente e para controlo de pagamentos.",
          isAdmin
            ? "The request will be permanently deleted from the database."
            : "The request will no longer appear in your list. The record is retained for the other party and for payment records.",
        )}
        cancelLabel={tr("Cancelar", "Cancel")}
        confirmLabel={tr("Eliminar", "Delete")}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={deleteRentalRequest}
      />
    </div>
  );
}

const panel = {
  position: "relative",
  inset: "auto",
  zIndex: "auto",
  overflow: "visible",
  padding: 0,
  width: "100%",
  maxWidth: "none",
  maxHeight: "none",
  margin: 0,
  boxSizing: "border-box",
  background: "transparent",
  color: "var(--tk-text,#18251e)",
  border: 0,
  borderRadius: 0,
  boxShadow: "none",
};
const requestPanel = {
  position: "fixed",
  top: "max(14px, env(safe-area-inset-top))",
  bottom: "calc(94px + env(safe-area-inset-bottom))",
  left: 10,
  right: 10,
  zIndex: 10010,
  margin: "0 auto",
  padding: "0 0 28px",
  width: "auto",
  maxWidth: 620,
  minWidth: 0,
  overflowY: "auto",
  overflowX: "hidden",
  boxSizing: "border-box",
  background: "var(--tk-surface,#fff)",
  color: "var(--tk-text,#18251e)",
  border: "1px solid var(--tk-border,rgba(0,0,0,.1))",
  borderRadius: 18,
  boxShadow: "0 12px 34px rgba(0,0,0,.3)",
  WebkitOverflowScrolling: "touch",
};
const grid = { display: "grid", gap: 10, marginTop: 12 };
const card = {
  display: "grid",
  gap: 5,
  padding: 12,
  border: "1px solid rgba(120,140,130,.3)",
  borderRadius: 12,
  background: "var(--tk-surface-soft,rgba(255,255,255,.9))",
  color: "inherit",
};
const catalogImage = {
  width: "100%",
  maxHeight: 180,
  objectFit: "cover",
  borderRadius: 10,
};
const backdrop = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: "calc(82px + env(safe-area-inset-bottom))",
  left: 0,
  zIndex: 10020,
  background: "rgba(0,0,0,.55)",
  display: "grid",
  placeItems: "center",
  padding: 16,
};
const modal = {
  position: "relative",
  width: "min(620px, calc(100vw - 24px))",
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100dvh - 118px - env(safe-area-inset-bottom))",
  overflowY: "auto",
  overflowX: "hidden",
  padding: 18,
  boxSizing: "border-box",
  background: "var(--tk-surface,#fff)",
  color: "var(--tk-text,#18251e)",
  border: "1px solid var(--tk-border,rgba(0,0,0,.14))",
  borderRadius: 18,
  boxShadow: "0 18px 50px rgba(0,0,0,.4)",
};
const actions = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 };
